import { useQuery } from "@tanstack/react-query";
import { Activity, Users, Bike, BadgeCheck, LogIn } from "lucide-react";
import { api } from "@/api";
import { useAuth } from "@/auth";
import { C } from "@/theme";
import { BarChart } from "@/charts";
import { Card, LoadingState, ErrorState, EmptyState, Badge } from "@/ui";
import { PageHeader } from "@/components/AppShell";

function Stat({ icon, label, value, testID }) {
  return (
    <div data-testid={testID} style={{ background: C.surfaceSecondary, borderColor: C.border }} className="flex-1 min-w-[150px] rounded-2xl border p-4">
      <div className="flex items-center gap-2" style={{ color: C.muted }}>{icon}<span className="text-xs font-semibold">{label}</span></div>
      <p style={{ color: C.onSurface }} className="text-3xl font-extrabold mt-1 tabular-nums">{value}</p>
    </div>
  );
}

export default function Analytics() {
  const { user } = useAuth();
  const role = user?.role;
  const q = useQuery({ queryKey: ["analytics-footfall"], queryFn: () => api.get(`/analytics/footfall?actor_role=${role}`), enabled: role === "Admin" });

  if (role !== "Admin") return <div className="p-6"><PageHeader title="App Footfall" /><EmptyState icon={<Activity size={28} color={C.muted} />} title="Admin only" subtitle="Usage analytics are available to administrators." testID="analytics-no-access" /></div>;
  if (q.isLoading) return <div className="p-6"><LoadingState /></div>;
  if (q.isError) return <div className="p-6"><PageHeader title="App Footfall" /><ErrorState onRetry={() => q.refetch()} /></div>;

  const d = q.data;
  const t = d.totals;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <PageHeader title="App Footfall" subtitle={`Usage over the last ${d.window_days} days`} />

      <div className="flex gap-3 flex-wrap mb-4">
        <Stat icon={<LogIn size={15} />} label="Logins" value={t.logins_30d} testID="stat-logins" />
        <Stat icon={<Users size={15} />} label="Active users" value={t.active_users_30d} testID="stat-active-users" />
        <Stat icon={<Bike size={15} />} label="Runs recorded" value={t.runs_30d} testID="stat-runs" />
        <Stat icon={<BadgeCheck size={15} />} label="Check rides" value={t.checkrides_30d} testID="stat-checkrides" />
      </div>

      <Card testID="chart-logins">
        <p style={{ color: C.onSurface }} className="font-extrabold mb-3">Logins per day <span style={{ color: C.muted }} className="font-semibold text-xs">(last {d.logins_series.length} days)</span></p>
        {d.logins_series.some((x) => x.value > 0) ? <BarChart data={d.logins_series} /> : <EmptyState icon={<LogIn size={24} color={C.muted} />} title="No logins yet" testID="logins-empty" />}
      </Card>

      <div className="h-3" />
      <Card testID="chart-runs">
        <p style={{ color: C.onSurface }} className="font-extrabold mb-3">Runs recorded per day <span style={{ color: C.muted }} className="font-semibold text-xs">(last {d.runs_series.length} days)</span></p>
        {d.runs_series.some((x) => x.value > 0) ? <BarChart data={d.runs_series} /> : <EmptyState icon={<Bike size={24} color={C.muted} />} title="No runs recorded yet" testID="runs-empty" />}
      </Card>

      <div className="h-3" />
      <Card testID="table-logins-by-user">
        <p style={{ color: C.onSurface }} className="font-extrabold mb-3">Logins by user</p>
        {(d.logins_by_user || []).length === 0 ? <EmptyState icon={<Users size={24} color={C.muted} />} title="No activity yet" testID="users-empty" /> : (
          <div className="flex flex-col gap-2">
            {d.logins_by_user.map((u, i) => (
              <div key={i} data-testid={`login-user-${i}`} className="flex items-center justify-between py-1.5" style={{ borderBottom: `1px solid ${C.border}` }}>
                <div className="flex items-center gap-2"><span style={{ color: C.onSurface }} className="text-sm font-semibold">{u.name}</span>{u.role ? <Badge label={u.role} variant="neutral" /> : null}</div>
                <span style={{ color: C.onSurface }} className="text-sm font-extrabold tabular-nums">{u.count}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
